import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { FloppyDisk, Clock, EnvelopeSimple, CalendarBlank, Signature, Globe, Sparkle } from "@phosphor-icons/react";

const TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Mexico_City", "Europe/London", "Europe/Paris",
  "Europe/Berlin", "Europe/Istanbul", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
  "Asia/Tokyo", "Australia/Sydney",
];

export default function Settings() {
  const [settings, setSettings] = useState({
    email_signature: "", timezone: "UTC",
    working_hours_start: 9, working_hours_end: 17,
    default_meeting_duration_minutes: 30,
  });
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [calendarAccounts, setCalendarAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, ea, ca] = await Promise.all([
          api.get("/settings"),
          api.get("/email/connection"),
          api.get("/calendar/accounts"),
        ]);
        setSettings((prev) => ({ ...prev, ...(s.data.settings || {}) }));
        if (ea.data.connection) setEmailAccounts([ea.data.connection]);
        setCalendarAccounts(ca.data.accounts || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="settings-page">
      <Toaster theme="dark" />
      <div className="mb-8">
        <div className="overline mb-2">// workspace settings</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Email signature */}
        <section className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="section-signature">
          <div className="flex items-center gap-2 mb-4">
            <Signature weight="bold" style={{ color: "var(--accent)" }} />
            <div className="overline">email signature</div>
          </div>
          <textarea
            data-testid="settings-signature"
            value={settings.email_signature || ""}
            onChange={(e) => setSettings({ ...settings, email_signature: e.target.value })}
            rows={5}
            placeholder="— Jane Doe, Founder @ MyNexusCard"
            className="input-base"
            style={{ fontFamily: "JetBrains Mono, monospace" }}
          />
          <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>
            Appended to every outbound email you send through MyNexusCard.
          </div>
        </section>

        {/* Timezone + hours */}
        <section className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="section-timezone">
          <div className="flex items-center gap-2 mb-4">
            <Globe weight="bold" style={{ color: "var(--accent)" }} />
            <div className="overline">timezone & working hours</div>
          </div>
          <label className="label-base">Timezone</label>
          <select
            data-testid="settings-timezone"
            value={settings.timezone || "UTC"}
            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
          >
            {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1">
              <label className="label-base">Start hour</label>
              <input
                type="number" min="0" max="23"
                data-testid="settings-wh-start"
                value={settings.working_hours_start ?? 9}
                onChange={(e) => setSettings({ ...settings, working_hours_start: Number(e.target.value) })}
                className="input-base"
              />
            </div>
            <div className="flex-1">
              <label className="label-base">End hour</label>
              <input
                type="number" min="1" max="24"
                data-testid="settings-wh-end"
                value={settings.working_hours_end ?? 17}
                onChange={(e) => setSettings({ ...settings, working_hours_end: Number(e.target.value) })}
                className="input-base"
              />
            </div>
          </div>
        </section>

        {/* Default email account */}
        <section className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="section-email-account">
          <div className="flex items-center gap-2 mb-4">
            <EnvelopeSimple weight="bold" style={{ color: "var(--accent)" }} />
            <div className="overline">default email account</div>
          </div>
          {emailAccounts.length === 0 && (
            <div className="text-sm" style={{ color: "var(--text-2)" }}>
              No email account connected yet. <a href="/dashboard/email-setup" style={{ color: "var(--accent)" }} className="underline">Connect one →</a>
            </div>
          )}
          {emailAccounts.map((ea, i) => (
            <div key={i} className="border p-3 flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="font-semibold">{ea.email}</div>
                <div className="overline">{ea.provider} · {ea.status}</div>
              </div>
              <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>DEFAULT</span>
            </div>
          ))}
        </section>

        {/* Default calendar */}
        <section className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="section-calendar-account">
          <div className="flex items-center gap-2 mb-4">
            <CalendarBlank weight="bold" style={{ color: "var(--accent)" }} />
            <div className="overline">default calendar</div>
          </div>
          {calendarAccounts.length === 0 && (
            <div>
              <div className="text-sm mb-3" style={{ color: "var(--text-2)" }}>
                No calendar connected. New meetings will be stored locally in MyNexusCard only — they won&apos;t appear on your Google / Outlook calendar until you connect one.
              </div>
              <button
                data-testid="connect-calendar-btn"
                className="btn-primary flex items-center gap-2"
                onClick={async () => {
                  try {
                    const r = await api.post("/calendar/accounts/connect/google");
                    if (r.data.mode === "dev") {
                      toast.message("Demo Mode", { description: r.data.message });
                    } else if (r.data.auth_url) {
                      window.location.href = r.data.auth_url;
                    }
                  } catch (e) { toast.error("Could not start calendar OAuth"); }
                }}
              >
                <CalendarBlank weight="bold" /> Connect Google Calendar
              </button>
            </div>
          )}
          {calendarAccounts.map((ca) => (
            <div key={ca.id} className="border p-3 flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="font-semibold">{ca.email}</div>
                <div className="overline">{ca.provider} · {ca.status}</div>
              </div>
              <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>DEFAULT</span>
            </div>
          ))}
          <div className="mt-4">
            <label className="label-base">Default meeting duration (minutes)</label>
            <input
              type="number" min="5" max="480"
              data-testid="settings-meeting-duration"
              value={settings.default_meeting_duration_minutes ?? 30}
              onChange={(e) => setSettings({ ...settings, default_meeting_duration_minutes: Number(e.target.value) })}
              className="input-base" style={{ width: 120 }}
            />
          </div>
        </section>
      </div>

      {/* Phase 4 — AI Preferences */}
      <section className="border p-6 mt-8" style={{ borderColor: "var(--border)" }} data-testid="section-ai-prefs">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle weight="bold" style={{ color: "var(--accent)" }} />
          <div className="overline">ai preferences</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label-base">AI automatic send mode</label>
            <select
              data-testid="settings-ai-automatic-send"
              value={settings.ai_automatic_send || "always_require_approval"}
              onChange={(e) => setSettings({ ...settings, ai_automatic_send: e.target.value })}
              className="input-base"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
            >
              <option value="always_require_approval">Always require approval (default)</option>
              <option value="auto_low_risk">Automatically send low-risk follow-ups</option>
              <option value="fully_automatic">Fully automatic</option>
            </select>
            <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>
              Controls whether AI-authored campaign steps send automatically or wait for your review.
            </div>
          </div>
          <div>
            <label className="label-base">Default AI tone</label>
            <select
              data-testid="settings-ai-default-tone"
              value={settings.ai_default_tone || "professional"}
              onChange={(e) => setSettings({ ...settings, ai_default_tone: e.target.value })}
              className="input-base"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
            >
              {["professional","friendly","conversational","direct","warm","short"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-base">Custom AI instructions</label>
            <textarea
              data-testid="settings-ai-custom-instructions"
              value={settings.ai_custom_instructions || ""}
              onChange={(e) => setSettings({ ...settings, ai_custom_instructions: e.target.value })}
              rows={3}
              placeholder="e.g., keep messages under 4 sentences, never offer discounts, always sign off as 'Best'"
              className="input-base"
              style={{ fontFamily: "JetBrains Mono, monospace" }}
            />
            <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>
              Applied to every AI draft and campaign {"{{ai_message}}"} rendering.
            </div>
          </div>
        </div>
      </section>

      <div className="mt-8 flex justify-end">
        <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
          <FloppyDisk weight="bold" /> {saving ? "Saving..." : "Save settings"}
        </button>
      </div>
    </div>
  );
}
